/**
 * SettingsPage — rota /settings.
 *
 * Fiel ao prototipo configuracoes.html (F3, B.48/B.49).
 * Blocos: Sua conta | Meu negocio | Preferencias | Salvar | Em breve (x2).
 *
 * Comportamento preservado do F1/F2:
 *   - logout via useAuth().logout() + navigate('/login')
 *   - dados reais de conta: email, workspaceName, workspaceLevel, role
 *   - dados de negocio (nome, unidades, tipo) sao locais ate backend expor endpoint
 *
 * Isolamento:
 *   - CSS proprio em screen-config.css (importado aqui, nao em global.css)
 *   - Componentes prefixados Config* (ConfigBlocoSoon, ConfigToggle, ConfigSelectField)
 *   - Nenhum arquivo fora de /settings editado, exceto i18n.ts (additive)
 *
 * FLAG_GAPS (para proximos itens):
 *   - [GAP-1] Campos nome, unidades, tipo, idioma, frequencia sao estado local —
 *             nao ha endpoint PATCH /me ou PATCH /tenant ainda. Salvar = noop com alerta.
 *   - [GAP-2] "Alterar senha" nao tem endpoint; placeholder de alerta usado.
 *   - [GAP-3] Notificacoes por e-mail: nao ha preferencia persistida na API.
 *   - [GAP-4] Nome do usuario nao vem de GET /me (apenas email vem); pre-preenche
 *             a partir do email (parte antes do @).
 */

import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { t } from '../lib/i18n';
import { useAuth, type Role, type TenantLevel } from '../lib/auth-context';
import { ConfigBlocoSoon } from '../components/ConfigBlocoSoon';
import { ConfigToggle } from '../components/ConfigToggle';
import { ConfigSelectField } from '../components/ConfigSelectField';
import '../styles/screen-config.css';

/* ----------------------------------------------------------------
   Helpers de label — herdados do F1/F2
   ---------------------------------------------------------------- */
function roleLabel(role: Role | null): string {
  switch (role) {
    case 'owner':
      return t('settings.role.owner');
    case 'admin':
      return t('settings.role.admin');
    case 'editor':
      return t('settings.role.editor');
    case 'member':
      return t('settings.role.member');
    case 'viewer':
      return t('settings.role.viewer');
    case 'api_consumer':
      return t('settings.role.apiConsumer');
    default:
      return '—';
  }
}

function levelLabel(level: TenantLevel | null): string {
  switch (level) {
    case 'root':
      return t('settings.workspaceLevel.root');
    case 'intermediate':
      return t('settings.workspaceLevel.intermediate');
    case 'leaf':
      return t('settings.workspaceLevel.leaf');
    default:
      return '—';
  }
}

/** Deriva nome de exibicao a partir do email enquanto nao houver campo proprio na API */
function deriveDisplayName(email: string | null): string {
  if (!email) return '';
  const local = email.split('@')[0] ?? '';
  // Capitaliza primeira letra e troca pontos/underscores por espaco
  return local.replace(/[._]/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}

/* ----------------------------------------------------------------
   Opcoes de select — via t() sem hardcode
   ---------------------------------------------------------------- */
const UNIT_OPTIONS = Array.from({ length: 9 }, (_, i) => ({
  value: String(i + 1),
  label: String(i + 1),
})).concat([{ value: '10+', label: '10 ou mais' }]);

export function SettingsPage(): JSX.Element {
  const navigate = useNavigate();
  const { email, activeTenantId, activeRole, memberships, mfaState, logout } = useAuth();

  /* Dados do espaco ativo */
  const activeMembership =
    memberships.find((m) => m.tenant_id === activeTenantId) ?? memberships[0] ?? null;
  const workspaceName =
    activeMembership?.tenant_name && activeMembership.tenant_name.length > 0
      ? activeMembership.tenant_name
      : '';
  const workspaceLevel = activeMembership?.tenant_level ?? null;
  const mfaActive = mfaState.enabled;

  /* ----------------------------------------------------------------
     Estado local dos campos — GAP-1: sem persistencia ate PATCH /me
     ---------------------------------------------------------------- */
  const [displayName, setDisplayName] = useState<string>(deriveDisplayName(email));
  const [bizName, setBizName] = useState<string>(workspaceName);
  // GAP-1 — unidades: sem campo real no backend ainda; estado vazio e honesto
  const [units, setUnits] = useState<string>('');
  const [bizType, setBizType] = useState<string>('food');
  const [lang, setLang] = useState<string>('pt-BR');
  const [notifEnabled, setNotifEnabled] = useState<boolean>(true);
  const [freq, setFreq] = useState<string>('weekly');

  const handleLogout = (): void => {
    logout();
    navigate('/login', { replace: true });
  };

  /* GAP-2: sem endpoint — noop com alerta */
  const handleChangePassword = (): void => {
    // eslint-disable-next-line no-alert
    window.alert(t('settings.changePasswordUnavailable'));
  };

  /* GAP-1: salvar e noop — backend nao tem endpoint ainda */
  const handleSave = (): void => {
    // eslint-disable-next-line no-alert
    window.alert(t('settings.saveUnavailable'));
  };

  /* Opcoes de tipo de negocio */
  const bizTypeOptions = [
    { value: 'food', label: t('settings.businessTypeFood') },
    { value: 'food-delivery', label: t('settings.businessTypeFoodDelivery') },
    { value: 'retail', label: t('settings.businessTypeRetail') },
    { value: 'services', label: t('settings.businessTypeServices') },
    { value: 'other', label: t('settings.businessTypeOther') },
  ];

  /* Opcoes de idioma */
  const langOptions = [
    { value: 'pt-BR', label: t('settings.langPtBR') },
    { value: 'en', label: t('settings.langEn') },
    { value: 'es', label: t('settings.langEs') },
  ];

  /* Opcoes de frequencia */
  const freqOptions = [
    { value: 'daily', label: t('settings.freqDaily') },
    { value: 'weekly', label: t('settings.freqWeekly') },
    { value: 'biweekly', label: t('settings.freqBiweekly') },
    { value: 'monthly', label: t('settings.freqMonthly') },
  ];

  return (
    <div className="cfg-page">
      {/* ===== PAGE HEADER ===== */}
      <header className="cfg-page-header">
        <div>
          <p className="cfg-page-eyebrow">{t('settings.pageEyebrow')}</p>
          <h1 className="cfg-page-title" id="settings-page-title">
            {t('settings.pageTitle')}
          </h1>
          <p className="cfg-page-subtitle">{t('settings.pageSubtitle')}</p>

          {/* Dados do espaco ativo — contexto do usuario logado */}
          {workspaceName.length > 0 ? (
            <p className="cfg-page-subtitle" style={{ marginTop: 4 }}>
              {workspaceName}
              {workspaceLevel !== null ? ` — ${levelLabel(workspaceLevel)}` : ''}
              {activeRole !== null ? ` · ${roleLabel(activeRole)}` : ''}
              {mfaActive ? ` · ${t('settings.mfaLabel')}: ${t('settings.mfaEnabled')}` : ''}
            </p>
          ) : null}
        </div>
      </header>

      <div className="cfg-stack">
        {/* ===== BLOCO 1: SUA CONTA ===== */}
        <section
          className="cfg-card"
          style={{ animationDelay: '60ms' }}
          aria-labelledby="cfg-account-title"
        >
          <div className="cfg-card-bar" aria-hidden="true" />
          <div className="cfg-card-inner">
            <p className="cfg-section-kicker">{t('settings.accountSectionKicker')}</p>
            <h2 className="cfg-section-title" id="cfg-account-title">
              <span className="material-symbols-rounded cfg-section-icon" aria-hidden="true">
                person
              </span>
              {t('settings.accountSectionTitle')}
            </h2>

            {/* Nome — GAP-4: derivado do email */}
            <div className="cfg-field">
              <label className="cfg-label" htmlFor="cfg-name">
                {t('settings.nameLabel')}
              </label>
              <input
                className="cfg-input"
                type="text"
                id="cfg-name"
                value={displayName}
                onChange={(e) => setDisplayName(e.target.value)}
                placeholder={t('settings.namePlaceholder')}
                autoComplete="name"
              />
            </div>

            {/* E-mail — readonly: nao ha endpoint de troca de email */}
            <div className="cfg-field">
              <label className="cfg-label" htmlFor="cfg-email">
                {t('settings.emailLabel')}
              </label>
              <input
                className="cfg-input"
                type="email"
                id="cfg-email"
                value={email ?? ''}
                readOnly
                aria-readonly="true"
                autoComplete="email"
              />
            </div>

            {/* Senha — GAP-2: sem endpoint */}
            <div className="cfg-field cfg-field--row">
              <div className="cfg-field-text">
                <div className="cfg-label">{t('settings.passwordLabel')}</div>
                <div className="cfg-hint" aria-label={t('settings.passwordLabel')}>
                  {t('settings.passwordMasked')}
                </div>
              </div>
              <button type="button" className="cfg-btn-outlined" onClick={handleChangePassword}>
                {t('settings.changePassword')}
              </button>
            </div>

            <div className="cfg-divider" role="separator" />

            {/* Sair */}
            <div className="cfg-field cfg-field--row cfg-field--last">
              <div className="cfg-field-text">
                <div className="cfg-label cfg-label--danger">{t('settings.logoutLabel')}</div>
                <div className="cfg-hint">{t('settings.logoutHint')}</div>
              </div>
              <button type="button" className="cfg-btn-danger" onClick={handleLogout}>
                {t('settings.logout')}
              </button>
            </div>
          </div>
        </section>

        {/* ===== BLOCO 2: MEU NEGOCIO ===== */}
        <section
          className="cfg-card"
          style={{ animationDelay: '130ms' }}
          aria-labelledby="cfg-business-title"
        >
          <div className="cfg-card-bar" aria-hidden="true" />
          <div className="cfg-card-inner">
            <p className="cfg-section-kicker">{t('settings.businessSectionKicker')}</p>
            <h2 className="cfg-section-title" id="cfg-business-title">
              <span className="material-symbols-rounded cfg-section-icon" aria-hidden="true">
                store
              </span>
              {t('settings.businessSectionTitle')}
            </h2>

            {/* Nome do negocio — pre-preenchido com tenant_name quando disponivel (GAP-1) */}
            <div className="cfg-field">
              <label className="cfg-label" htmlFor="cfg-biz-name">
                {t('settings.businessNameLabel')}
              </label>
              <input
                className="cfg-input"
                type="text"
                id="cfg-biz-name"
                value={bizName}
                onChange={(e) => setBizName(e.target.value)}
                placeholder={t('settings.businessNamePlaceholder')}
              />
            </div>

            {/* Unidades — GAP-1: sem dado real; placeholder honesto ate PATCH /tenant */}
            <ConfigSelectField
              id="cfg-units"
              label={t('settings.businessUnitsLabel')}
              options={UNIT_OPTIONS}
              value={units}
              onChange={setUnits}
              placeholder={t('settings.unitsPlaceholder')}
            />

            {/* Tipo de negocio */}
            <ConfigSelectField
              id="cfg-biz-type"
              label={t('settings.businessTypeLabel')}
              options={bizTypeOptions}
              value={bizType}
              onChange={setBizType}
              isLast
            />
          </div>
        </section>

        {/* ===== BLOCO 3: PREFERENCIAS ===== */}
        <section
          className="cfg-card"
          style={{ animationDelay: '200ms' }}
          aria-labelledby="cfg-pref-title"
        >
          <div className="cfg-card-bar" aria-hidden="true" />
          <div className="cfg-card-inner">
            <p className="cfg-section-kicker">{t('settings.prefSectionKicker')}</p>
            <h2 className="cfg-section-title" id="cfg-pref-title">
              <span className="material-symbols-rounded cfg-section-icon" aria-hidden="true">
                tune
              </span>
              {t('settings.prefSectionTitle')}
            </h2>

            {/* Idioma */}
            <ConfigSelectField
              id="cfg-lang"
              label={t('settings.langLabel')}
              options={langOptions}
              value={lang}
              onChange={setLang}
            />

            {/* Notificacoes por e-mail — GAP-3: sem persistencia */}
            <div className="cfg-field cfg-field--row">
              <div className="cfg-field-text">
                <div className="cfg-label">{t('settings.notifLabel')}</div>
                <div className="cfg-hint">{t('settings.notifHint')}</div>
              </div>
              <ConfigToggle
                id="cfg-notif"
                ariaLabel={t('settings.notifLabel')}
                checked={notifEnabled}
                onChange={setNotifEnabled}
              />
            </div>

            {/* Frequencia dos resumos */}
            <ConfigSelectField
              id="cfg-freq"
              label={t('settings.freqLabel')}
              options={freqOptions}
              value={freq}
              onChange={setFreq}
              isLast
            />
          </div>
        </section>

        {/* ===== LINHA SALVAR ===== */}
        {/* GAP-1: noop ate PATCH /me disponivel */}
        <div className="cfg-save-row" style={{ animationDelay: '260ms' }}>
          <button type="button" className="cfg-btn-save" onClick={handleSave}>
            <span className="material-symbols-rounded cfg-btn-save-icon" aria-hidden="true">
              check
            </span>
            <span className="cfg-btn-save-label">{t('settings.saveButton')}</span>
          </button>
        </div>

        {/* ===== EM BREVE: Integracoes ===== */}
        <ConfigBlocoSoon
          icon="extension"
          titleKey="settings.soonIntegTitle"
          descKey="settings.soonIntegDesc"
          animationDelay={320}
        />

        {/* ===== EM BREVE: Equipe ===== */}
        <ConfigBlocoSoon
          icon="group"
          titleKey="settings.soonTeamTitle"
          descKey="settings.soonTeamDesc"
          animationDelay={370}
        />

        {/* Bloco "Seu espaco de trabalho" removido — nao existe no prototipo (D-1).
            Os dados de workspace (nome, nivel, papel) sao expostos no page header
            quando disponíveis. Link de faturamento fica em /billing. */}
      </div>
    </div>
  );
}
